import React from 'react';
import { ArrowRight, CheckCircle, Clock, Shield, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Hero Section for A/B test variant B
 *
 * Changes from variant A:
 * - Client-centered title with immediate benefit
 * - Reassuring subtitle with social proof stats
 * - Founder/expert photo for authenticity
 * - Clear, repeated CTA
 * - Trust badges more prominent
 */
const HeroSectionB = ({ onCTAClick }) => {
  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left column - Text content */}
          <div className="text-center lg:text-left">
            {/* Main title - problem-focused */}
            <h1
              data-testid="hero-title-b"
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 leading-tight"
              style={{ color: '#1e3a8a' }}
            >
              On vous réclame une dette ?
            </h1>

            {/* Subtitle with value proposition */}
            <p className="text-base sm:text-lg text-gray-700 mb-6 leading-relaxed">
              Déposez votre dossier : nous l'analysons <strong>gratuitement</strong> et rédigeons
              pour vous <strong>vos courriers de réponse, prêts à poster</strong>.
            </p>

            {/* Trust badges - horizontal on mobile, stacked on desktop */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-3 mb-6">
              <div className="flex items-center bg-green-50 border border-green-200 rounded-full px-3 py-1.5 text-sm">
                <CheckCircle size={16} className="text-green-600 mr-1.5" />
                <span className="text-green-800 font-medium">100% gratuit</span>
              </div>
              <div className="flex items-center bg-blue-50 border border-blue-200 rounded-full px-3 py-1.5 text-sm">
                <Clock size={16} className="text-blue-600 mr-1.5" />
                <span className="text-blue-800 font-medium">Resultat en 3 min</span>
              </div>
              <div className="flex items-center bg-purple-50 border border-purple-200 rounded-full px-3 py-1.5 text-sm">
                <Shield size={16} className="text-purple-600 mr-1.5" />
                <span className="text-purple-800 font-medium">Confidentiel</span>
              </div>
            </div>

            {/* Primary CTA */}
            <Button
              data-testid="cta-analyze-button-b"
              size="lg"
              onClick={() => onCTAClick('Me défendre')}
              className="w-full sm:w-auto px-8 py-6 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105"
              style={{ backgroundColor: '#1e3a8a', color: 'white' }}
            >
              Me défendre
              <ArrowRight className="ml-2" size={24} />
            </Button>

            {/* No credit card message */}
            <p className="text-sm text-gray-500 mt-3 flex items-center justify-center lg:justify-start">
              <CheckCircle size={14} className="text-green-500 mr-1.5" />
              Aucune carte bancaire requise
            </p>
          </div>

          {/* Right column - Founder/Expert section */}
          <div className="flex flex-col items-center lg:items-end">
            {/* Expert card */}
            <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm border border-gray-100">
              {/* Avatar placeholder - use real photo in production */}
              <div className="flex items-center mb-4">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold"
                  style={{ backgroundColor: '#1e3a8a' }}
                >
                  MW
                </div>
                <div className="ml-4">
                  <p className="font-semibold text-gray-900">Mathieu Welsch</p>
                  <p className="text-sm text-gray-600">Fondateur</p>
                </div>
              </div>

              {/* Quote */}
              <blockquote className="text-gray-700 italic border-l-4 border-blue-200 pl-4 mb-4">
                "Trop de gens paient des dettes qu'ils ne doivent plus. Notre IA détecte les prescriptions et irrégularités en quelques minutes."
              </blockquote>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                <div className="text-center">
                  <p className="text-2xl font-bold" style={{ color: '#1e3a8a' }}>2000+</p>
                  <p className="text-xs text-gray-600">Dossiers analyses</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold" style={{ color: '#1e3a8a' }}>68%</p>
                  <p className="text-xs text-gray-600">Situations clarifiées</p>
                </div>
              </div>
            </div>

            {/* Social proof */}
            <div className="mt-4 flex items-center text-sm text-gray-600">
              <Users size={16} className="mr-2 text-gray-400" />
              <span>12 analyses aujourd'hui</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSectionB;
